/**
 * Группировка Whisper-сегментов в читаемые абзацы для MVP-UI.
 *
 * Идея близка к worker `buildUtterances`: соседние реплики одного спикера
 * (или поток без спикеров) сливаются, длинные куски режутся по длине /
 * границе предложения — без изменения текста.
 */

export type TranscriptParagraphSegment = {
  id: string;
  index: number;
  startMs: number;
  endMs: number;
  text: string;
  /** Присутствует только после опубликованной диаризации (может быть null). */
  speaker?: { id: string; index: number; displayName: string } | null | undefined;
};

export type TranscriptParagraph = {
  /** id первого сегмента — стабильный ключ и якорь таймкода. */
  id: string;
  startMs: number;
  endMs: number;
  text: string;
  segmentIds: readonly string[];
};

/** Мягкий целевой размер абзаца (символы). */
export const PARAGRAPH_SOFT_MIN_CHARS = 800;
export const PARAGRAPH_SOFT_MAX_CHARS = 1200;

/** Пауза между сегментами, после которой без спикеров начинаем новый абзац. */
export const PARAGRAPH_PAUSE_GAP_MS = 1500;

export function joinSegmentTexts(parts: readonly string[]): string {
  return parts
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ')
    .replace(/\s+/gu, ' ')
    .trim();
}

function endsWithSentenceBoundary(text: string): boolean {
  return /[.!?…]["»')\]]?\s*$/u.test(text.trimEnd());
}

function speakerKey(segment: TranscriptParagraphSegment): string {
  if (segment.speaker === undefined) {
    return '';
  }
  if (segment.speaker === null) {
    return 'null';
  }
  return segment.speaker.id;
}

function hasDiarizationSpeakerField(segments: readonly TranscriptParagraphSegment[]): boolean {
  return segments.some((segment) => segment.speaker !== undefined);
}

type Draft = {
  segmentIds: string[];
  texts: string[];
  startMs: number;
  endMs: number;
  firstId: string;
};

function flushDraft(draft: Draft | null, into: TranscriptParagraph[]): void {
  if (draft === null) {
    return;
  }
  const text = joinSegmentTexts(draft.texts);
  if (text.length === 0) {
    return;
  }
  into.push({
    id: draft.firstId,
    startMs: draft.startMs,
    endMs: draft.endMs,
    text,
    segmentIds: [...draft.segmentIds],
  });
}

function startDraft(segment: TranscriptParagraphSegment): Draft {
  return {
    segmentIds: [segment.id],
    texts: [segment.text],
    startMs: segment.startMs,
    endMs: segment.endMs,
    firstId: segment.id,
  };
}

function appendToDraft(draft: Draft, segment: TranscriptParagraphSegment): void {
  draft.segmentIds.push(segment.id);
  draft.texts.push(segment.text);
  draft.endMs = Math.max(draft.endMs, segment.endMs);
}

function shouldSplitForLength(currentText: string, nextText: string): boolean {
  const joined = joinSegmentTexts([currentText, nextText]);
  if (joined.length > PARAGRAPH_SOFT_MAX_CHARS) {
    // Превысили максимум — режем между исходными сегментами.
    return true;
  }
  // В зоне ~800–1200 предпочитаем границу законченного предложения.
  if (
    currentText.length >= PARAGRAPH_SOFT_MIN_CHARS &&
    endsWithSentenceBoundary(currentText)
  ) {
    return true;
  }
  return false;
}

/**
 * Группирует сегменты в абзацы.
 *
 * - С диаризацией: смена speaker → новый абзац; длинная речь одного спикера
 *   дополнительно режется по 800–1200 символов.
 * - Без спикеров: пауза > PARAGRAPH_PAUSE_GAP_MS или лимит длины.
 */
export function groupTranscriptParagraphs(
  segments: readonly TranscriptParagraphSegment[],
): TranscriptParagraph[] {
  if (segments.length === 0) {
    return [];
  }

  const useSpeakers = hasDiarizationSpeakerField(segments);
  const paragraphs: TranscriptParagraph[] = [];
  let draft: Draft | null = null;
  let currentSpeaker: string | null = null;

  for (const segment of segments) {
    if (draft === null) {
      draft = startDraft(segment);
      currentSpeaker = useSpeakers ? speakerKey(segment) : null;
      continue;
    }

    const currentText = joinSegmentTexts(draft.texts);
    const speakerChanged =
      useSpeakers && speakerKey(segment) !== currentSpeaker;
    const gapMs = segment.startMs - draft.endMs;
    const pauseBreak = !useSpeakers && gapMs > PARAGRAPH_PAUSE_GAP_MS;
    const lengthBreak = shouldSplitForLength(currentText, segment.text);

    if (speakerChanged || pauseBreak || lengthBreak) {
      flushDraft(draft, paragraphs);
      draft = startDraft(segment);
      currentSpeaker = useSpeakers ? speakerKey(segment) : null;
      continue;
    }

    appendToDraft(draft, segment);
  }

  flushDraft(draft, paragraphs);
  return paragraphs;
}
